import { QueryClient } from '@tanstack/react-query';

/**
 * React Query (TanStack Query) configuration
 *
 * Provides optimized defaults for caching, refetching, and error handling
 * for all API requests in the application.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh (5 minutes)
      staleTime: 1000 * 60 * 5,

      // Cache time: how long inactive data stays in cache (10 minutes)
      gcTime: 1000 * 60 * 10,

      // Retry failed requests up to 2 times with exponential backoff
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 2;
      },

      // Refetch on window focus (helps keep data fresh)
      refetchOnWindowFocus: true,

      // Don't refetch on reconnect by default
      refetchOnReconnect: false,

      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,

      // Global error handler for mutations
      onError: (error) => {
        console.error('Mutation error:', error);
        // You can add toast notifications or global error handling here
      },
    },
  },
});

/**
 * Query keys factory
 * Provides consistent query key structure across the application
 */
export const queryKeys = {
  // Authentication
  auth: {
    user: ['auth', 'user'] as const,
    session: ['auth', 'session'] as const,
  },

  // Permits
  permits: {
    all: ['permits'] as const,
    search: (params: unknown) => ['permits', 'search', params] as const,
    detail: (id: string) => ['permits', 'detail', id] as const,
    fees: (id: string, params: unknown) => ['permits', 'fees', id, params] as const,
  },

  // Billing
  billing: {
    all: ['billing'] as const,
    balance: ['billing', 'balance'] as const,
    transactions: (params?: unknown) => ['billing', 'transactions', params] as const,
    transaction: (id: string) => ['billing', 'transaction', id] as const,
  },

  // Admin
  admin: {
    all: ['admin'] as const,
    users: (params?: unknown) => ['admin', 'users', params] as const,
    user: (id: number) => ['admin', 'user', id] as const,
    usageAnalytics: ['admin', 'analytics', 'usage'] as const,
    revenueAnalytics: (params?: unknown) => ['admin', 'analytics', 'revenue', params] as const,
    successRateAnalytics: (params?: unknown) => ['admin', 'analytics', 'success-rates', params] as const,
    municipalities: (staleOnly?: boolean) => ['admin', 'municipalities', staleOnly] as const,
    municipalityStats: ['admin', 'municipalities', 'statistics'] as const,
    verificationHistory: (params?: unknown) => ['admin', 'verification', 'history', params] as const,
    searchData: (params?: unknown) => ['admin', 'searchData', params] as const,
    searchDetail: (id: string) => ['admin', 'searchData', 'detail', id] as const,
  },
} as const;
